import {Checkbox, FormControlLabel, IconButton} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import VLFTextField from "@/components/Input/TextField";

export const defaultSportingPeriods = () =>
	[1, 2].map((number) => ({number, durationMinutes: 45, isExtraTime: false}));

export const editionSportingDefaults = (edition) => ({
	duration: (
		edition?.defaultSportingPeriods ?? defaultSportingPeriods()
	).reduce((sum, p) => sum + Number(p.durationMinutes), 0),
	sportingPeriods: (
		edition?.defaultSportingPeriods ?? defaultSportingPeriods()
	).map((p) => ({...p})),
	allowReentry: Boolean(edition?.defaultAllowReentry),
});

export const sportingPeriodsError = (periods) => {
	if (periods == null) return "";
	if (!periods.length || periods[0]?.isExtraTime)
		return "Indica almeno un tempo regolamentare.";
	if (
		periods.some(
			(p) =>
				!Number.isInteger(Number(p.durationMinutes)) ||
				Number(p.durationMinutes) <= 0,
		)
	)
		return "Ogni tempo deve avere una durata positiva in minuti interi.";
	if (
		periods.reduce((sum, p) => sum + Number(p.durationMinutes), 0) > 2147483647
	)
		return "La durata sportiva totale è troppo grande.";
	return "";
};

export const sportingPayload = (fields) => ({
	sportingPeriods:
		fields.sportingPeriods == null
			? null
			: fields.sportingPeriods.map((p, i) => ({
					number: i + 1,
					durationMinutes: Number(p.durationMinutes),
					isExtraTime: Boolean(p.isExtraTime),
				})),
	allowReentry: Boolean(fields.allowReentry),
});

export const sportingStructureLocked = (match) =>
	Boolean(
		match?.sportingPeriods &&
			(match.matchEvents?.length ||
				match.numEvaluations > 0 ||
				match.matchGpsStats?.length),
	);

export const sportingSummary = (periods) =>
	periods?.length
		? periods
				.map(
					(p) =>
						`${p.isExtraTime ? "Supplementare" : "Tempo"} ${p.number}: ${p.durationMinutes}′`,
				)
				.join(" · ")
		: "Non definita";

export default function MatchSportingConfig({
	periods,
	allowReentry,
	onChange,
	locked = false,
}) {
	const update = (next) =>
		onChange({sportingPeriods: next.map((p, i) => ({...p, number: i + 1}))});
	const error = sportingPeriodsError(periods);
	return (
		<div className="match-sporting-config">
			<FormControlLabel
				control={
					<Checkbox
						checked={periods != null}
						disabled={locked}
						onChange={(e) =>
							onChange({
								sportingPeriods: e.target.checked
									? defaultSportingPeriods()
									: null,
							})
						}
					/>
				}
				label="Configura tempi di gioco"
			/>
			{periods == null ? (
				<p>
					Durata sportiva non definita. Gli orari del calendario restano
					separati.
				</p>
			) : (
				<>
					{[false, true].map((extra) => (
						<div key={String(extra)}>
							<div className="d-flex align-items-center justify-content-between">
								<span>
									{extra ? "Tempi supplementari" : "Tempi regolamentari"} (
									{
										periods.filter((p) => Boolean(p.isExtraTime) === extra)
											.length
									}
									)
								</span>
								<IconButton
									size="small"
									title={
										extra
											? "Aggiungi supplementare"
											: "Aggiungi tempo regolamentare"
									}
									aria-label={
										extra
											? "Aggiungi supplementare"
											: "Aggiungi tempo regolamentare"
									}
									disabled={locked}
									onClick={() =>
										update(
											extra
												? [...periods, {durationMinutes: 15, isExtraTime: true}]
												: [
														...periods.filter((p) => !p.isExtraTime),
														{durationMinutes: 45, isExtraTime: false},
														...periods.filter((p) => p.isExtraTime),
													],
										)
									}
								>
									<AddIcon />
								</IconButton>
							</div>
							<div className="row g-2 mx-0">
								{periods.map(
									(p, i) =>
										Boolean(p.isExtraTime) === extra && (
											<div className="col-12 col-md-6 col-xl-4" key={p.number}>
												<div className="d-flex align-items-center gap-1">
													<VLFTextField
														label={`${p.isExtraTime ? "Supplementare" : "Tempo"} ${p.number} (minuti)`}
														name={`sport-duration-${p.number}`}
														value={p.durationMinutes ?? ""}
														type="number"
														size="small"
														inputProps={{min: 1, step: 1}}
														disabled={locked}
														onChange={(e) =>
															update(
																periods.map((item, index) =>
																	index === i
																		? {...item, durationMinutes: e.target.value}
																		: item,
																),
															)
														}
														error={
															!Number.isInteger(Number(p.durationMinutes)) ||
															Number(p.durationMinutes) <= 0
														}
													/>
													<IconButton
														size="small"
														color="error"
														title={`Rimuovi tempo ${p.number}`}
														aria-label={`Rimuovi tempo ${p.number}`}
														disabled={
															locked ||
															(!extra &&
																periods.filter((item) => !item.isExtraTime)
																	.length === 1)
														}
														onClick={() =>
															update(periods.filter((_, index) => index !== i))
														}
													>
														<CloseIcon />
													</IconButton>
												</div>
											</div>
										),
								)}
							</div>
						</div>
					))}
					<p>
						<strong>
							Durata sportiva:{" "}
							{error
								? "—"
								: periods.reduce(
										(sum, p) => sum + Number(p.durationMinutes),
										0,
									)}{" "}
							minuti
						</strong>
						. Recupero escluso; supplementari inclusi.
					</p>
				</>
			)}
			{error && <p className="text-danger">{error}</p>}
			{locked && (
				<p>
					I tempi sono già utilizzati da eventi o statistiche: la struttura non
					può essere cambiata.
				</p>
			)}
			<FormControlLabel
				control={
					<Checkbox
						checked={Boolean(allowReentry)}
						onChange={(e) => onChange({allowReentry: e.target.checked})}
					/>
				}
				label="Consenti rientri nelle sostituzioni"
			/>
		</div>
	);
}
